import { GOOGLE_ERRORS, GOOGLE_SCOPE, SDK_UNAVAILABLE } from './constants';
import { loadSdk } from './loadSdk';
import { providerError } from './providerError';
import type { WindowWithGoogleIdentity } from './types';

const googleError = (code: string) =>
  providerError(code, `Sign in with Google failed: ${code}`);

/**
 * Runs Sign in with Google and resolves with an authorization code.
 *
 * This flow does not use the Google button, so it has the same steps as the
 * Apple flow: a click, the provider window, then a credential. The credential
 * is an authorization code. `POST /auth/v1/exchange` redeems it at Google, so
 * the browser never holds an ID token.
 *
 * This flow needs no nonce. `initCodeClient` has no field for one, and the code
 * belongs to the client id. Google redeems it one time only.
 *
 * `ux_mode: 'popup'` sends the code back to this document. Set no
 * `redirect_uri` and register none. The backend redeems with `postmessage`.
 */
const signInWithGoogle = async ({
  clientId,
  sdkSrc,
}: {
  clientId: string;
  /** The URL the site settings give. The caller checks that one exists. */
  sdkSrc: string;
}): Promise<string> => {
  await loadSdk(sdkSrc);

  const oauth2 = (window as unknown as WindowWithGoogleIdentity).google
    ?.accounts?.oauth2;

  if (!oauth2) {
    throw providerError(SDK_UNAVAILABLE, 'Sign in with Google is unavailable');
  }

  return new Promise<string>((resolve, reject) => {
    oauth2
      .initCodeClient({
        client_id: clientId,
        scope: GOOGLE_SCOPE,
        ux_mode: 'popup',
        callback: response => {
          if (response.code) {
            resolve(response.code);

            return;
          }

          // A window that answers with no code is a refused consent. Use that
          // code when Google names none.
          reject(googleError(response.error ?? GOOGLE_ERRORS.CONSENT_DENIED));
        },
        // Google calls this in place of `callback` when no window opens. This
        // is the only way it reports a blocked popup.
        error_callback: error => reject(googleError(error.type)),
      })
      .requestCode();
  });
};

export { signInWithGoogle };
